import { boschAdapter } from './boschAdapter'
import { dmpAdapter } from './dmpAdapter'
import { genericAdapter } from './genericAdapter'
import { PLANE_COLORS } from '../config/presentation'

/**
 * Adapters convert vendor-specific API payloads into the common device shape
 * consumed by the floor-plan components. Their order is intentional:
 *
 * - DMP handles DMP panel devices plus virtual health and action slots.
 * - Bosch handles Bosch panel and panel-device payloads.
 * - Generic is the safe fallback for cameras, alarms, and other vendors.
 */
const adapters = [dmpAdapter, boschAdapter, genericAdapter]

/** Maps virtual alarm definitions returned by the unit endpoint. */
const mapAlarms = (items = []) => items.reduce((result, alarm) => ({
  ...result,
  [alarm.id]: {
    name: alarm.data?.name ?? alarm.id,
    color: alarm.data?.color ?? PLANE_COLORS.offline,
    text_color: alarm.data?.text_color ?? PLANE_COLORS.textOnColor,
    icon: alarm.data?.icon ?? null,
    color_icon: alarm.data?.color_icon ?? PLANE_COLORS.textOnColor,
    acronym: alarm.data?.acronym ?? '?',
    active_alarm: alarm.data?.active_alarm ?? false,
    can_update_alarm: alarm.can_update_alarm ?? false,
    dispatch_url: alarm.actions?.dispatch_alarm ?? null,
    cancel_url: alarm.actions?.cancel_alarm ?? null,
  },
}), {})

/** Maps the four virtual health indicators generated for every DMP panel. */
const mapDmpStatuses = (items = []) => items.reduce((result, status) => ({
  ...result,
  [status.id]: {
    panel_id: status.panel_id ?? null,
    panel_proxy_id: status.panel_proxy_id ?? null,
    status_key: status.data?.status_key ?? null,
    name: status.data?.name ?? status.id,
    icon: status.data?.icon ?? null,
    color: status.data?.color ?? null,
    color_icon: status.data?.color_icon ?? null,
    text_color: status.data?.text_color ?? null,
    status: status.data?.status ?? null,
    value: status.data?.value ?? null,
    should_pulse: status.data?.should_pulse === true,
    isCustomIcon: ['tamper', 'acPower', 'battery'].includes(status.data?.status_key),
  },
}), {})

/** Maps virtual panel actions while preserving the action URLs from the API. */
const mapDmpActions = (items = []) => items.reduce((result, action) => ({
  ...result,
  [action.id]: {
    name: action.data?.name ?? action.id,
    icon: action.data?.icon ?? null,
    color: action.data?.color ?? null,
    color_icon: action.data?.color_icon ?? null,
    text_color: action.data?.text_color ?? null,
    actions: action.actions ?? {},
  },
}), {})

/** Extracts the panel proxy IDs used to associate placed devices with DMP. */
const getDmpPanelProxyIds = (statuses = []) => ([
  ...new Set(
    statuses
      .map((status) => status.panel_proxy_id)
      .filter((panelProxyId) => panelProxyId !== null && panelProxyId !== undefined)
      .map(String)
  ),
])

/** Creates an O(1) lookup for camera alarm state by proxy ID. */
const mapCameraAlarmStates = (items = []) => items.reduce((result, alarm) => ({
  ...result,
  [alarm.id_camera_proxy]: alarm,
}), {})

/**
 * Converts the unit relation response into lookups used by vendor adapters.
 */
export const createDeviceRelations = (response) => {
  const devicesPayload = response?.data?.devices
  const devices = Array.isArray(devicesPayload)
    ? devicesPayload[0] ?? {}
    : devicesPayload ?? {}

  return {
    alarms: mapAlarms(devices.alarms),
    cameraAlarmStates: mapCameraAlarmStates(devices.camera_alarm_states),
    dmpStatuses: mapDmpStatuses(devices.dmp_panel_status),
    dmpActions: mapDmpActions(devices.dmp_panel_actions),
    dmpPanelProxyIds: getDmpPanelProxyIds(devices.dmp_panel_status),
  }
}

/**
 * Maps an API floor-device into the common UI contract using its vendor adapter.
 */
export const normalizeFloorDevice = (floorDevice, context) => {
  const adapter = adapters.find((candidate) => candidate.matches(floorDevice, context)) ?? genericAdapter
  return adapter.normalize(floorDevice, context)
}

export const normalizeFloorPlane = (plane, relations) => ({
  id: plane.id,
  name: plane.name,
  path: plane.path,
  devices: (plane.devices ?? []).map((floorDevice) => normalizeFloorDevice(floorDevice, {
    planeId: plane.id,
    planeDevices: plane.devices ?? [],
    relations,
  })),
})
